// HABITACIÓN (V2/V5) — tarjeta de habitación con una FILA por cama/residente.
// Distingue sitio, habitación, cama y persona. No hereda los verdes del prototipo: la cobertura
// se presenta POR CAPACIDAD con sus estados canónicos, incluido «no confirmable». Una cama vacía
// válida se muestra vacía; no se inventa residente. Ausencia de actividad no significa que la
// persona esté bien.
import { ShieldAlert, ShieldCheck, HelpCircle, Wrench, UserX, Circle } from "lucide-react";
import { verboEvento } from "@/lib/consolaV3/meta";
import { visualPriority as acentoPersona, eventSentence } from "@/components/visualV2/presentation";
import { etiquetaCasosHabitacion, lineaEstadoHabitacion } from "@/lib/radar/instR02";

const horaCorta = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false });
};

const ICONO_COB = { ok: ShieldCheck, bad: ShieldAlert, warn: ShieldAlert, muted: HelpCircle };
const COLOR_COB = { ok: "gv2-ok", bad: "gv2-tech", warn: "gv2-tech", muted: "gv2-muted-text" };
// Tope de filas de ámbito de habitación dentro de una tarjeta: el carril de atención es el canal
// para el conjunto completo.
const MAX_CASOS_HABITACION = 3;

function Cobertura({ cob, testId }) {
  if (!cob) {
    return (
      <span data-testid={testId} title="No hay contexto de cobertura autorizado para esta cama."
        className="shrink-0 text-slate-300"><Circle className="h-3.5 w-3.5" /></span>
    );
  }
  const Icono = ICONO_COB[cob.tono] || HelpCircle;
  const detalle = cob.detalle.map((d) => `${d.capacidad}: ${d.texto} (${d.equipos} equipo(s) conocido(s), ${d.origen})`).join(" · ");
  return (
    <span data-testid={testId} data-estado={cob.estado} title={`${detalle}. El conteo es de EQUIPOS por capacidad; no confirma protección individual simultánea.`}
      className={`shrink-0 ${COLOR_COB[cob.tono] || COLOR_COB.muted}`}>
      <Icono className="h-3.5 w-3.5" />
    </span>
  );
}

function FilaCama({ cama, compacta, onAbrirCaso, onFicha }) {
  const caso = cama.caso;
  const ac = caso ? acentoPersona(caso.prioridad) : null;
  const sinResidente = cama.sinResidente;
  const abrir = () => {
    if (caso) onAbrirCaso(caso);
    else if (cama.persona_id) onFicha(cama.persona_id);
  };
  return (
    <div data-testid={`radar-inst-cama-${cama.cama_id || cama.persona_id}`}
      data-con-caso={caso ? "si" : "no"} data-priority={ac?.key}
      role={sinResidente ? undefined : "button"} tabIndex={sinResidente ? undefined : 0}
      onClick={sinResidente ? undefined : abrir}
      onKeyDown={(e) => { if (!sinResidente && e.key === "Enter") abrir(); }}
      className={`${caso ? "gv2-card" : "gv2-bed"} rounded-lg px-2.5 ${compacta ? "py-1.5" : "py-2"} transition-colors ${sinResidente ? "" : "cursor-pointer"} focus:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3]`}
      >
      <div className="flex items-start justify-between gap-2">
        <p className="flex min-w-0 items-baseline gap-1.5">
          {cama.etiqueta && (
            <span className="gv2-bed-tag shrink-0">
              {cama.etiqueta}
            </span>
          )}
          <span className={`min-w-0 truncate text-[15px] font-semibold ${sinResidente ? "text-slate-400" : "text-slate-800"}`}>
            {sinResidente ? "Cama sin residente asignado" : (cama.persona_nombre || "Residente sin identificar")}
          </span>
        </p>
        <Cobertura cob={cama.cobertura} testId={`radar-inst-cobertura-${cama.cama_id || cama.persona_id}`} />
      </div>

      {caso ? (
        <p data-testid={`radar-inst-cama-caso-${cama.cama_id || cama.persona_id}`}
          className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] font-bold leading-tight">
          <span className="gv2-priority-badge">{ac.label}</span>
          <span>· {eventSentence(caso.verbo || verboEvento(caso.tipo))}</span>
          <span className="gv2-state">{caso.estadoLabel}</span>
          {caso.desde && <span className="font-mono tabular-nums">· Desde {horaCorta(caso.desde)}</span>}
          {cama.casos.length > 1 && (
            <span data-testid={`radar-inst-cama-multicaso-${cama.cama_id || cama.persona_id}`}>
              · +{cama.casos.length - 1} {cama.casos.length - 1 === 1 ? "caso más en esta cama" : "casos más en esta cama"}
            </span>
          )}
        </p>
      ) : cama.casosEnOtraUbicacion > 0 ? (
        // P3 · la persona tiene casos, pero NO fueron capturados en esta cama: se avisa sin
        // moverlos aquí. Siguen accesibles en el carril de atención y en Central.
        <p data-testid={`radar-inst-cama-otra-ubicacion-${cama.cama_id || cama.persona_id}`}
          title="Esos casos se registraron en otra ubicación y se muestran donde ocurrieron. Siguen accesibles en la atención del centro."
          className="mt-0.5 truncate text-[11px] font-semibold leading-tight text-[#B45309]">
          {cama.casosEnOtraUbicacion} {cama.casosEnOtraUbicacion === 1 ? "caso registrado en otra ubicación" : "casos registrados en otra ubicación"}
        </p>
      ) : compacta ? null : cama.actividad ? (
        <p className="mt-0.5 truncate text-[11px] leading-tight text-[#0284C7]">
          <span className="font-mono tabular-nums">{horaCorta(cama.actividad.hora)}</span>
          {cama.actividad.tipo ? ` · ${verboEvento(cama.actividad.tipo)}` : ""}
        </p>
      ) : cama.ultimoHecho ? (
        <p className="mt-0.5 truncate text-[11px] leading-tight text-slate-400">
          <span className="font-mono tabular-nums">{horaCorta(cama.ultimoHecho.hora)}</span>
          {` · ${verboEvento(cama.ultimoHecho.tipo)}`}
        </p>
      ) : sinResidente ? null : (
        <p className="mt-0.5 text-[11px] leading-tight text-slate-400">Sin actividad observada recientemente</p>
      )}
    </div>
  );
}

export default function HabitacionR02({ h, compacta, resaltada, onAbrirCaso, onFicha, innerRef,
  hayMas = false, noConfirmado = false }) {
  const camas = h.camas || [];
  // P4-C · se cuentan CASOS reales (una cama con dos casos aporta dos), no camas con algún caso.
  const conCaso = h.totalCasos ?? 0;
  const casosHabitacion = h.casosHabitacion || [];
  const etiquetaCasos = etiquetaCasosHabitacion(conCaso, { hayMas, noConfirmado });
  // Línea superior de estado: sólo casos abiertos conocidos de ESTA habitación (ver instR02.js).
  const linea = lineaEstadoHabitacion(h, { hayMas, noConfirmado });
  return (
    <section ref={innerRef} data-testid={`radar-inst-habitacion-${h.zona_id || "sin-ubicacion"}`}
      data-resaltada={resaltada ? "si" : "no"} data-linea={linea.estado}
      className={`gv2-room relative flex min-w-0 flex-col p-3 ${resaltada ? "gv2-room-resaltada" : ""}`}>
      <span data-testid={`radar-inst-linea-${h.zona_id || "sin-ubicacion"}`} data-estado={linea.estado}
        title={linea.titulo} className={`gv2-room-line ${linea.clase}`} />
      <header className="gv2-room-head mb-2 flex items-baseline justify-between gap-2 pb-2">
        <p className="flex min-w-0 items-baseline gap-1.5">
          <span className="gv2-room-name text-[14px] font-bold text-[#012472]">{h.nombre}</span>
          {h.tipo && <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{h.tipo}</span>}
        </p>
        <span data-testid={`radar-inst-conteo-${h.zona_id || "sin-ubicacion"}`}
          title={hayMas ? "Carga parcial: quedan casos por leer del centro. No es el total definitivo."
            : noConfirmado ? "La lectura del centro no se pudo confirmar en este ciclo." : undefined}
          className="shrink-0 text-right text-[11px] tabular-nums text-slate-400">
          {h.sinUbicacion
            ? `${camas.length} ${camas.length === 1 ? "residente" : "residentes"}`
            : `${camas.length} ${camas.length === 1 ? "cama" : "camas"}`}
          {etiquetaCasos ? ` · ${etiquetaCasos}` : ""}
        </span>
      </header>

      {h.sinUbicacion && (
        <p className="mb-1.5 flex items-start gap-1.5 rounded-lg bg-slate-50 px-2 py-1 text-[11px] leading-snug text-slate-500">
          <UserX className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Residentes autorizados de este centro sin cama verificable. Ubicación no confirmada.
        </p>
      )}

      <div className={compacta ? "flex flex-col gap-1" : "flex flex-col gap-1.5"}>
        {camas.length === 0 && (
          <p className="px-1 py-2 text-[11px] text-slate-400">Habitación sin camas registradas.</p>
        )}
        {camas.map((c) => (
          <FilaCama key={c.cama_id || c.persona_id} cama={c} compacta={compacta}
            onAbrirCaso={onAbrirCaso} onFicha={onFicha} />
        ))}
      </div>

      {casosHabitacion.length > 0 && (
        <div className="gv2-room-head mt-2 space-y-1.5 border-b-0 border-t pt-2">
          {/* La tarjeta nunca crece sin límite: se leen los de mayor prioridad y el resto se
              alcanza en la atención del centro, que conserva todos los casos autorizados. */}
          {casosHabitacion.slice(0, MAX_CASOS_HABITACION).map((c) => (
            <button key={c.alarma_id} type="button" data-testid={`radar-inst-hab-caso-${c.alarma_id}`} data-priority={acentoPersona(c.prioridad).key}
              onClick={() => onAbrirCaso(c)}
              title="Caso registrado con ámbito de habitación: no se atribuye a ninguna cama concreta."
              className="gv2-card flex w-full items-center gap-1.5 rounded-lg px-1.5 py-2 text-left text-xs font-semibold">
              {c.es_tecnica ? <Wrench className="h-3 w-3 shrink-0" /> : <ShieldAlert className="h-3 w-3 shrink-0" />}
              <span className="min-w-0 break-words">
                <span className="gv2-priority-badge">{acentoPersona(c.prioridad).label}</span> {eventSentence(c.verbo)} · {c.persona_nombre || "ámbito de habitación"} · {c.estadoLabel}
              </span>
            </button>
          ))}
          {casosHabitacion.length > MAX_CASOS_HABITACION && (
            <p data-testid={`radar-inst-hab-mas-${h.zona_id}`}
              title="El resto se consulta en la atención del centro, que conserva todos los casos autorizados."
              className="px-1.5 pt-0.5 text-[11px] font-semibold text-slate-500">
              +{casosHabitacion.length - MAX_CASOS_HABITACION} casos más con ámbito de esta habitación
            </p>
          )}
        </div>
      )}
    </section>
  );
}
